import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
    DAILY_ENGAGE_TARGET,
    DEFAULT_GROW_DAILY_TARGET,
    getLapState,
    lapLabel,
} from "./taskLapModel";

function renderCenterLabel(label, progress) {
    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");
    return (
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
            <tspan
                x="50%"
                dy="-0.2em"
                fontSize="16"
                fill={isFirstInfluencerDomain ? "#0074e4" : "#fe60a1"}
            >
                {label}
            </tspan>
            <tspan
                x="50%"
                dy="1.2em"
                fontSize="14"
                fill={isFirstInfluencerDomain ? "#0074e4" : "#fe60a1"}
            >
                {progress}%
            </tspan>
        </text>
    );
}

export default function TaskDonutCharts({
    validatedProfile,
    engagePercent = 0,
    engageCompletedToday = 0,
    engageDailyTarget = DAILY_ENGAGE_TARGET,
    growPercent = 0,
    growCompletedToday = 0,
    growDailyTarget = DEFAULT_GROW_DAILY_TARGET,
    growWeeklyUsed = 0,
    growWeeklyHard = 125,
    growMonthlyUsed = 0,
    growMonthlyHard = 500,
    growMonthPercent = 0,
    postPercent: postPercentProp = 0,
    activeTab, // <--- Pass in "engage" | "post" | "grow"
}) {
    // Determine color scheme based on hostname
    // (Be sure to guard for SSR: window may be undefined if running on server)
    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");

    // ENGAGE and GROW now share ONE lap model (taskLapModel.js): a full circle is one lap,
    // the ring visually restarts at 0% for the second lap and turns "slow down" red, and the
    // centre label picks up a ² marker. The only per-flow difference is the LAP SIZE — a
    // frontend constant for engage, the server-sent daily target for grow — because that is
    // where their limit accounting genuinely differs.
    //
    // Both are derived from today's completed count so optimistic per-click updates reflect
    // immediately; the server percent is only a fallback for the pre-count render (count 0).
    // NOTE: grow deliberately no longer takes Math.max(dailyProgress, growPercent). That
    // max() was a floor, and a floor pins the ring at 100% once the daily target is hit —
    // which would make a second lap impossible to display. It also never protected against
    // the fail-closed backend path, since gcGetTasks omits growPercent and completedToday.grow
    // together (docs/grow-connect-limits.md §6), so both sides go missing at once.
    const engageLap = getLapState({
        completed: engageCompletedToday,
        lapSize: engageDailyTarget,
        serverPercent: engagePercent,
    });
    const growLap = getLapState({
        completed: growCompletedToday,
        lapSize: growDailyTarget,
        serverPercent: growPercent,
    });

    // Gradient colors for the active portion
    const gradientStart = isFirstInfluencerDomain ? "#0074e4" : "#fe60a1";
    const gradientEnd = isFirstInfluencerDomain ? "#67aaf1" : "#ff8765";

    // Aggressive second-lap colors — same on both domains, signals "slow down".
    const secondLapStart = "#e63946";
    const secondLapEnd = "#ff7b54";

    // For the active ring leftover portion
    const leftoverActiveColor = isFirstInfluencerDomain ? "#d0e8fb" : "#fde6e6";
    const secondLapLeftoverColor = "#fde2dc";

    const engageFillId = engageLap.isFinalLap ? "fillColorSecondLap" : "fillColor";
    const growFillId = growLap.isFinalLap ? "fillColorSecondLap" : "fillColor";
    const engageLeftoverColor = engageLap.isFinalLap
        ? secondLapLeftoverColor
        : leftoverActiveColor;
    const growLeftoverColor = growLap.isFinalLap
        ? secondLapLeftoverColor
        : leftoverActiveColor;

    // Calculate POST donut progress. POST is binary (posted today or not), so it has no lap.
    let postProgress = postPercentProp;
    if (validatedProfile?.[0]?.lastPostDate) {
        const lastPostDate = validatedProfile[0].lastPostDate;
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        postProgress = Math.max(postProgress, new Date(lastPostDate).getTime() > oneDayAgo ? 100 : 0);
    }

    // Get day name for tooltip
    const getDayName = () => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[new Date().getDay()];
    };

    // Tooltip payload shared by both lap rings, so ENGAGE stops showing a bare
    // "Remaining: 96%" with no lap context. Grow adds its hard-cap lines on top.
    const lapTooltipMeta = (title, lapState, extras) => ({
        type: "lap",
        title,
        dayName: getDayName(),
        completedInLap: lapState.completedInLap,
        lapSize: lapState.lapSize,
        lapIndex: lapState.lapIndex,
        lapsPerDay: lapState.lapsPerDay,
        percent: lapState.visualPercent,
        remainingInLap: lapState.remainingInLap,
        completedToday: lapState.completed,
        dayTotal: lapState.dayTotal,
        ...extras,
    });

    const engageMeta = lapTooltipMeta("ENGAGE", engageLap);
    const growMeta = lapTooltipMeta("GROW", growLap, {
        weeklyCurrent: growWeeklyUsed,
        weeklyTotal: growWeeklyHard,
        monthlyCurrent: growMonthlyUsed,
        monthlyTotal: growMonthlyHard,
        monthPercent: growMonthPercent,
    });

    // Prepare data sets with additional info for tooltips
    const engageData = [
        { name: "Progress", value: engageLap.visualPercent, ...engageMeta },
        { name: "Remaining", value: 100 - engageLap.visualPercent, ...engageMeta },
    ];
    const postData = [
        { name: "Progress", value: postProgress, type: "post" },
        { name: "Remaining", value: 100 - postProgress, type: "post" },
    ];
    const growData = [
        { name: "Progress", value: growLap.visualPercent, ...growMeta },
        { name: "Remaining", value: 100 - growLap.visualPercent, ...growMeta },
    ];

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length > 0) {
            const data = payload[0].payload;

            if (data.type === "lap") {
                return (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                    }}>
                        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                            {data.title} - {data.dayName}
                        </p>
                        <p style={{ margin: '0 0 3px 0' }}>
                            Lap {data.lapIndex} of {data.lapsPerDay}: {data.completedInLap} / {data.lapSize} tasks
                        </p>
                        <p style={{ margin: '0 0 3px 0' }}>
                            Progress: {data.percent}%
                        </p>
                        {data.remainingInLap > 0 ? (
                            <p style={{ margin: '0 0 3px 0', color: '#666' }}>
                                Remaining this lap: {data.remainingInLap}
                            </p>
                        ) : null}
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#888' }}>
                            Today: {data.completedToday} / {data.dayTotal}
                        </p>
                        {data.weeklyTotal ? (
                            <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#888' }}>
                                Weekly limit: {data.weeklyCurrent} / {data.weeklyTotal}
                            </p>
                        ) : null}
                        {data.monthlyTotal ? (
                            <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#888' }}>
                                Monthly limit: {data.monthlyCurrent} / {data.monthlyTotal} ({data.monthPercent}%)
                            </p>
                        ) : null}
                    </div>
                );
            }

            // Default tooltip for post (binary, no lap)
            return (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                }}>
                    <p style={{ margin: 0 }}>
                        {data.name}: {data.value}%
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div key={activeTab} style={{ width: 300, height: 300 }}>
            <ResponsiveContainer>
                <PieChart>
                    <defs>
                        <linearGradient
                            id="fillColor"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={gradientStart}
                                stopOpacity={1}
                            />
                            <stop
                                offset="100%"
                                stopColor={gradientEnd}
                                stopOpacity={1}
                            />
                        </linearGradient>
                        {/* Shared by whichever ring is on its second lap — engage, grow, or both. */}
                        <linearGradient
                            id="fillColorSecondLap"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={secondLapStart}
                                stopOpacity={1}
                            />
                            <stop
                                offset="100%"
                                stopColor={secondLapEnd}
                                stopOpacity={1}
                            />
                        </linearGradient>
                    </defs>

                    {/* GROW outer ring */}
                    <Pie
                        data={growData}
                        dataKey="value"
                        innerRadius="85%"
                        outerRadius="100%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "grow"
                                ? renderCenterLabel(
                                    lapLabel("GROW", growLap),
                                    growLap.visualPercent
                                )
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "grow"
                                    ? `url(#${growFillId})`
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "grow"
                                    ? growLeftoverColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    {/* POST middle ring */}
                    <Pie
                        data={postData}
                        dataKey="value"
                        innerRadius="64%"
                        outerRadius="79%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "post"
                                ? renderCenterLabel("POST", postProgress)
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "post"
                                    ? "url(#fillColor)"
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "post"
                                    ? leftoverActiveColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    {/* ENGAGE inner ring */}
                    <Pie
                        data={engageData}
                        dataKey="value"
                        innerRadius="43%"
                        outerRadius="58%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "engage"
                                ? renderCenterLabel(
                                    lapLabel("ENGAGE", engageLap),
                                    engageLap.visualPercent
                                )
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "engage"
                                    ? `url(#${engageFillId})`
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "engage"
                                    ? engageLeftoverColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}
